import React, { useState } from 'react';
import { useStytchB2BClient } from '@stytch/react/b2b';

console.log('🔄 SIGNUP: Fresh component loaded');

const SignUp = ({
  intermediateSessionToken,
  emailAddress,
  eulaVersion,
  onOrganizationCreated,
  onError
}) => {
  console.log('🔍 SIGNUP: Rendering with intermediate session token');
  
  const stytch = useStytchB2BClient();
  
  // Profile fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(emailAddress || '');
  const [phone, setPhone] = useState('');
  
  // Organization fields
  const [organizationName, setOrganizationName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('United States');
  
  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  console.log('🔍 SIGNUP: Stytch client available:', !!stytch);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    console.log('🚀 SIGNUP: Starting organization creation and signup...');
    console.log('🔍 SIGNUP: Profile:', { firstName, lastName, email, phone });
    console.log('🔍 SIGNUP: Organization:', { organizationName, addressLine1, city, state, zipCode, country });
    console.log('💾 SIGNUP: Backend will store minimal data in Stytch, complete data in DynamoDB');

    if (!stytch) {
      console.error('❌ SIGNUP: No Stytch client');
      setError('Authentication not available. Please refresh.');
      setIsLoading(false);
      return;
    }

    if (!intermediateSessionToken) {
      console.error('❌ SIGNUP: No intermediate session token');
      setError('Authentication token missing. Please restart the signup process.');
      setIsLoading(false);
      return;
    }

    try {
      console.log('🏢 SIGNUP: Starting organization creation...');

      // Create organization slug from organization name
      const organizationSlug = organizationName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special chars except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

      console.log('🔍 SIGNUP: Generated slug:', organizationSlug);

      console.log('🔑 SIGNUP: Using intermediate session token to create organization');

      // Prepare the API request - backend will write to both Stytch (minimal) and DynamoDB (complete)
      const apiRequest = {
        intermediate_session_token: intermediateSessionToken,
        organization_name: organizationName,
        organization_slug: organizationSlug,
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone: phone,
        address_street: addressLine1,
        address_street2: addressLine2 || '',
        address_city: city,
        address_state: state,
        address_zip_code: zipCode,
        address_country: country,
        eula_version: eulaVersion
      };

      console.log('🚀 SIGNUP: Calling backend API:', JSON.stringify(apiRequest, null, 2));

      // Call backend endpoint - it will create in both Stytch and DynamoDB atomically
      const baseURL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${baseURL}/admin/organizations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(apiRequest)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const orgResponse = await response.json();

      console.log('✅ SIGNUP: Organization created successfully:', orgResponse);
      console.log('🔍 SIGNUP: Organization ID:', orgResponse.organization?.organization_id);
      console.log('🔍 SIGNUP: Member session:', orgResponse.member_session);
      console.log('💾 SIGNUP: Data stored - Stytch: minimal (name, slug), DynamoDB: complete (all fields)');
      
      // Organization creation automatically creates session - user is logged in
      console.log('✅ SIGNUP: Organization created and user authenticated');
      
      // Notify parent component of successful organization creation
      if (onOrganizationCreated) {
        onOrganizationCreated(orgResponse);
      }
      
      setSuccess(true);
      
    } catch (error) {
      console.error('❌ SIGNUP: Error:', error);
      
      let errorMessage = 'Signup failed. Please try again.';
      
      if (error.error_type === 'organization_slug_already_exists') {
        errorMessage = 'Organization name already exists. Please try a different name.';
      } else if (error.error_type === 'member_exists') {
        errorMessage = 'Account already exists. Please try logging in instead.';
      } else if (error.error_type === 'billing_not_verified_for_email') {
        errorMessage = 'Email delivery temporarily unavailable. Please try again later or contact support.';
      } else if (error.error_type === 'no_match_for_provided_magic_link_url') {
        errorMessage = 'Configuration error: This domain is not authorized for signup. Please contact support.';
      } else if (error.error_message || error.message) {
        errorMessage = 'Signup failed: ' + (error.error_message || error.message);
      }
      
      setError(errorMessage);
      
      if (onError) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-text-primary mb-2">Welcome!</h1>
            <p className="text-text-secondary">Your organization <strong>{organizationName}</strong> has been created!</p>
          </div>

          {/* Success Card */}
          <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-accent-amber/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎉</span>
              </div>
              <h3 className="text-lg font-medium text-text-primary mb-2">Setup Complete!</h3>
              <p className="text-text-secondary mb-4">
                You're now logged into your new organization. Welcome to Lumoz AI!
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">LUMOZ AI</h1>
          <p className="text-text-secondary">Complete your signup</p>
        </div>

        {/* Signup Form */}
        <div className="bg-background-secondary border border-accent-lavender/30 rounded-lg p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4 p-3 bg-accent-amber/10 border border-accent-amber/30 rounded text-accent-amber text-sm">
            Setting up organization for <strong>{emailAddress}</strong>
          </div>

          <form onSubmit={handleSignUp} className="space-y-6">
            <div className="space-y-6">
              {/* Profile Section */}
              <div className="space-y-4">
                <h3 className="text-base font-medium text-text-secondary">Profile</h3>
              
                {/* First and Last Name on one line */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                      placeholder="Enter first name"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                      placeholder="Enter last name"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Email and Phone on one line */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      className="w-full px-3 py-2 bg-background-secondary border border-accent-lavender/20 rounded text-text-secondary cursor-not-allowed"
                      disabled={true}
                      readOnly={true}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                      placeholder="Enter phone number"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Company Section */}
              <div className="space-y-4">
                <h3 className="text-base font-medium text-text-secondary">Company</h3>
              
                {/* Company Name on one line */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                    placeholder="Enter company name"
                    required
                    disabled={isLoading}
                  />
                </div>

                {/* Address Line 1 and 2 on one line */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                      placeholder="Street address"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Address Line 2 (Optional)
                    </label>
                    <input
                      type="text"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                      placeholder="Apartment, suite, etc."
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* City, State, ZIP, and Country on one line */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                      placeholder="City"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                      placeholder="State"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary placeholder-gray-500 focus:outline-none focus:border-accent-primary-base"
                      placeholder="ZIP"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Country
                    </label>
                    <select 
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="w-full px-3 py-2 bg-background-primary border border-accent-lavender/30 rounded text-text-primary focus:outline-none focus:border-accent-primary-base"
                      required
                      disabled={isLoading}
                    >
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Australia">Australia</option>
                      <option value="China">China</option>
                      <option value="Germany">Germany</option>
                      <option value="France">France</option>
                      <option value="India">India</option>
                      <option value="Japan">Japan</option>
                      <option value="Singapore">Singapore</option>
                      <option value="Netherlands">Netherlands</option>
                      <option value="Switzerland">Switzerland</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-accent-primary-base text-text-inverse py-2 px-4 rounded font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating Organization...' : 'Complete Signup'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUp;